import type { ClientRow } from '@busy/api';

// Shared helpers for the optional mm/dd birthday field (migration 0062).
// Two responsibilities:
//   1) `daysUntilBirthday(month, day)` — "how many days from now until
//      this birthday rolls around?" Wraps the year boundary correctly
//      (e.g. on Dec 30, a Jan 2 birthday returns 3, not 363).
//   2) `formatBirthdayShort(month, day, language)` — locale-friendly
//      "Jun 14" style display. Never includes a year (deliberate — see
//      migration 0062 header).

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** Days from "today" until the next occurrence of this mm/dd. 0 = today,
 *  1 = tomorrow, etc. Wraps the year boundary correctly. Returns null
 *  if either input is null. Feb 29 birthdays fall back to Mar 1 in
 *  non-leap years (a tiny edge case — most barbers won't notice, and
 *  Mar 1 is the conventional rollover the U.S. uses). */
export function daysUntilBirthday(
  month: number | null,
  day: number | null,
  now: Date = new Date(),
): number | null {
  if (month == null || day == null) return null;
  // Local-date math intentionally — barber's wall clock is the right
  // frame for "is this client's birthday today / this week."
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  // Try this year first; if it's already passed, try next year.
  // JS Date auto-rolls invalid days (e.g. Feb 29 in 2025 becomes Mar 1),
  // which is exactly the leap-day fallback behavior we want.
  let candidate = new Date(today.getFullYear(), month - 1, day);
  if (candidate.getTime() < today.getTime()) {
    candidate = new Date(today.getFullYear() + 1, month - 1, day);
  }
  return Math.round((candidate.getTime() - today.getTime()) / MS_PER_DAY);
}

/** "Jun 14" — short month + day, no year. Locale-aware (en-US / es-US).
 *  Returns null if either input is null. */
export function formatBirthdayShort(
  month: number | null,
  day: number | null,
  language: 'en' | 'es',
): string | null {
  if (month == null || day == null) return null;
  // Year doesn't matter for display; use 2000 (leap year) so Feb 29 renders.
  const d = new Date(2000, month - 1, day);
  const locale = language === 'es' ? 'es-US' : 'en-US';
  return d.toLocaleDateString(locale, { month: 'short', day: 'numeric' });
}

/** Predicate: does this client have a birthday within the next `window`
 *  days (inclusive of today and the boundary day). `window=0` means
 *  birthdays falling exactly today. */
export function isBirthdayWithinDays(
  client: Pick<ClientRow, 'birthday_month' | 'birthday_day'>,
  window: number,
  now?: Date,
): boolean {
  const n = daysUntilBirthday(client.birthday_month, client.birthday_day, now);
  if (n == null) return false;
  return n >= 0 && n <= window;
}
